/**
 * FlowSelectorStep Component
 * ==========================
 * First screen when both KYC and KYB are enabled.
 * User selects "Individual" or "Business" verification.
 */

import React, { useState, useEffect } from 'react';
import { User, Building2, ChevronRight, Lock, Loader2 } from 'lucide-react';

export default function FlowSelectorStep({
  onSelect,
  config = {},
  branding = {},
  whiteLabel = false,
}) {
  const [animateIn, setAnimateIn] = useState(false);
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  // Customizable labels from config
  const title = config.title || 'How would you like to verify?';
  const individualLabel = config.individualLabel || 'Individual';
  const individualDescription = config.individualDescription || 'Personal account verification';
  const businessLabel = config.businessLabel || 'Business';
  const businessDescription = config.businessDescription || 'Company or organization verification';

  const handleSelect = async (flowType) => {
    setSelecting(flowType);
    try {
      await onSelect(flowType);
    } catch (err) {
      console.error('Failed to select flow:', err);
      setSelecting(null);
    }
  };

  return (
    <div className={`sdk-step flow-selector-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        <div className="step-icon-wrapper">
          <div className="step-icon-bg" />
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="Logo" className="sdk-tenant-logo" />
          ) : (
            <div className="flow-selector-dual-icon">
              <User size={20} />
              <Building2 size={20} />
            </div>
          )}
        </div>
        <h2>{title}</h2>
        <p className="step-subtitle">
          Choose the type of verification that applies to you
        </p>
      </div>

      <div className="flow-selector-options">
        {/* Individual Option */}
        <button
          className={`flow-selector-option ${selecting === 'individual' ? 'selecting' : ''}`}
          onClick={() => handleSelect('individual')}
          disabled={selecting !== null}
        >
          <div className="flow-option-icon individual">
            <User size={28} />
          </div>
          <div className="flow-option-content">
            <span className="flow-option-label">{individualLabel}</span>
            <span className="flow-option-desc">{individualDescription}</span>
          </div>
          <div className="flow-option-arrow">
            {selecting === 'individual' ? (
              <Loader2 size={18} className="spin" />
            ) : (
              <ChevronRight size={18} />
            )}
          </div>
        </button>

        {/* Business Option */}
        <button
          className={`flow-selector-option ${selecting === 'business' ? 'selecting' : ''}`}
          onClick={() => handleSelect('business')}
          disabled={selecting !== null}
        >
          <div className="flow-option-icon business">
            <Building2 size={28} />
          </div>
          <div className="flow-option-content">
            <span className="flow-option-label">{businessLabel}</span>
            <span className="flow-option-desc">{businessDescription}</span>
          </div>
          <div className="flow-option-arrow">
            {selecting === 'business' ? (
              <Loader2 size={18} className="spin" />
            ) : (
              <ChevronRight size={18} />
            )}
          </div>
        </button>
      </div>

      <div className="flow-selector-footer">
        <Lock size={12} />
        <span>
          {whiteLabel
            ? (branding?.company_name ? `Secured by ${branding.company_name}` : 'Secure verification')
            : `Secured with ${branding?.name || 'TrustGate'}`}
        </span>
      </div>

      <style>{flowSelectorStyles}</style>
    </div>
  );
}

const flowSelectorStyles = `
  .flow-selector-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem 1.5rem;
    max-width: 480px;
    margin: 0 auto;
  }

  .flow-selector-dual-icon {
    display: flex;
    gap: 0.25rem;
    color: var(--sdk-primary, #0D9488);
  }

  .flow-selector-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    margin-top: 1.5rem;
  }

  .flow-selector-option {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
  }

  .flow-selector-option:hover:not(:disabled) {
    border-color: var(--sdk-primary, #0D9488);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .flow-selector-option:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .flow-selector-option.selecting {
    border-color: var(--sdk-primary, #0D9488);
    background: rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.04);
  }

  .flow-option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    flex-shrink: 0;
  }

  .flow-option-icon.individual {
    background: linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%);
    color: #0284C7;
  }

  .flow-option-icon.business {
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    color: #059669;
  }

  .flow-option-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .flow-option-label {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
  }

  .flow-option-desc {
    font-size: 0.875rem;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .flow-option-arrow {
    color: var(--sdk-text-secondary, #9CA3AF);
    transition: color 0.2s, transform 0.2s;
  }

  .flow-selector-option:hover:not(:disabled) .flow-option-arrow {
    color: var(--sdk-primary, #0D9488);
    transform: translateX(2px);
  }

  .flow-selector-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--sdk-border, #E5E7EB);
    width: 100%;
    justify-content: center;
    font-size: 0.75rem;
    color: var(--sdk-text-secondary, #9CA3AF);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .flow-selector-footer svg {
    opacity: 0.7;
  }

  /* Animation */
  .flow-selector-step.animate-in .flow-selector-option {
    animation: slideUp 0.3s ease forwards;
  }

  .flow-selector-step.animate-in .flow-selector-option:nth-child(2) {
    animation-delay: 0.1s;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Initial hidden state before animation */
  .flow-selector-option {
    opacity: 0;
  }

  .flow-selector-step.animate-in .flow-selector-option {
    opacity: 1;
  }
`;
